
import {
  defineComponent, computed, PropType,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Dropdown from 'primevue/dropdown';
import MultiSelect from 'primevue/multiselect';
import InputText from 'primevue/inputtext';
import Button from 'primevue/button';

interface AvailableRelation {
  propertyName: string;
  targetSchema: string;
  targetLabel: string;
}
interface AvailableColumn {
  field: string;
  header: string;
  type?: string;
  section: string;
  relationKey?: string;
  isId?: boolean;
}
export interface RawCustomDataMeta {
  idDefinition: number;
  schemaName: string;
  availableRelations: AvailableRelation[];
  availableColumns: AvailableColumn[];
  defaultColumns: string[];
  defaultDateColumn?: string;
}
export interface RawCustomDataFilter {
  field: string;
  operator: string;
  value: string | number | boolean;
}
export interface RawCustomDataSelection {
  selectedColumns: string[];
  enabledRelations: string[];
  userFilters?: RawCustomDataFilter[];
  dateColumn?: string;
}

const UNARY_OPERATORS = new Set(['is_null', 'is_not_null']);

export default defineComponent({
  name: 'RawCustomDataTableWidgetDataConfig',
  components: {
    Dropdown,
    MultiSelect,
    InputText,
    Button,
  },
  props: {
    meta: {
      type: Object as PropType<RawCustomDataMeta>,
      required: true,
    },
    selection: {
      type: Object as PropType<RawCustomDataSelection>,
      required: true,
    },
  },
  emits: ['update:selection'],
  setup(props, { emit }) {
    const { t } = useI18n();

    const selectedColumns = computed(() => props.selection.selectedColumns ?? []);
    const userFilters = computed(() => props.selection.userFilters ?? []);

    // Toutes les colonnes activables sont listées sans pré-condition (la
    // relation correspondante sera JOIN à l'exécution si une colonne ou un
    // filtre la cible — déduction automatique).
    const allColumns = computed(() => props.meta.availableColumns);

    /**
     * Construit les groupes (main / relations m2o / meta) pour un sous-ensemble
     * de colonnes filtrées. Réutilisé par le MultiSelect Colonnes, le Dropdown
     * Date column et le Dropdown Field des filtres user — tous présentent la
     * même hiérarchie pour distinguer la source d'une colonne (notamment pour
     * éviter la confusion sur les colonnes homonymes type `created_at` qui
     * existent à la fois sur la table principale et sur les relations).
     */
    const buildColumnGroups = (predicate: (c: AvailableColumn) => boolean) => {
      const groups: Array<{ label: string; items: AvailableColumn[] }> = [];
      const main = allColumns.value.filter((c) => c.section === 'main' && predicate(c));
      if (main.length > 0) {
        groups.push({ label: t('customStats.rawCustomData.columns.groupMain'), items: main });
      }
      for (const rel of props.meta.availableRelations) {
        const items = allColumns.value.filter(
          (c) => c.relationKey === rel.propertyName && predicate(c),
        );
        if (items.length > 0) {
          groups.push({
            label: t('customStats.rawCustomData.columns.groupRelation', {
              name: rel.targetLabel || rel.propertyName,
            }),
            items,
          });
        }
      }
      const meta = allColumns.value.filter((c) => c.section === 'meta' && predicate(c));
      if (meta.length > 0) {
        groups.push({ label: t('customStats.rawCustomData.columns.groupMeta'), items: meta });
      }
      return groups;
    };

    const visibleColumnGroups = computed(() => buildColumnGroups(() => true));

    const dateColumnGroups = computed(() =>
      buildColumnGroups((c) => c.type === 'datetime' || c.type === 'date'),
    );

    /**
     * `enabledRelations` est auto-déduit : on collecte les `relationKey`
     * uniques des colonnes sélectionnées + des filtres user. Le backend
     * recevra cette liste et ne fera de JOIN que pour les relations
     * réellement utiles.
     */
    const computeEnabledRelations = (
      cols: string[],
      filters: RawCustomDataFilter[],
    ): string[] => {
      const keys = new Set<string>();
      const lookupCol = (path: string) => props.meta.availableColumns
        .find((c) => c.field === path);
      for (const path of cols) {
        const k = lookupCol(path)?.relationKey;
        if (k) keys.add(k);
      }
      for (const f of filters) {
        const k = lookupCol(f.field)?.relationKey;
        if (k) keys.add(k);
      }
      return Array.from(keys);
    };

    const filterOperators = computed(() => [
      { value: 'eq', label: t('customStats.filterOp.eq') },
      { value: 'neq', label: t('customStats.filterOp.neq') },
      { value: 'gt', label: t('customStats.filterOp.gt') },
      { value: 'gte', label: t('customStats.filterOp.gte') },
      { value: 'lt', label: t('customStats.filterOp.lt') },
      { value: 'lte', label: t('customStats.filterOp.lte') },
      { value: 'contains', label: t('customStats.filterOp.contains') },
      { value: 'starts_with', label: t('customStats.filterOp.starts_with') },
      { value: 'ends_with', label: t('customStats.filterOp.ends_with') },
      { value: 'is_null', label: t('customStats.filterOp.is_null') },
      { value: 'is_not_null', label: t('customStats.filterOp.is_not_null') },
    ]);

    const isUnaryOperator = (op: string) => UNARY_OPERATORS.has(op);

    /**
     * Émet la sélection avec `enabledRelations` recalculé automatiquement
     * depuis les colonnes/filtres choisis. Le backend recevra la liste
     * exacte de relations à JOIN — pas de toggle UI à gérer côté user.
     */
    const updateSelection = (patch: Partial<RawCustomDataSelection>) => {
      const next: RawCustomDataSelection = { ...props.selection, ...patch };
      next.enabledRelations = computeEnabledRelations(
        next.selectedColumns ?? [],
        next.userFilters ?? [],
      );
      emit('update:selection', next);
    };

    function onUpdateColumns(values: string[] | null) {
      updateSelection({ selectedColumns: values ?? [] });
    }

    function onUpdateDateColumn(value: string | null) {
      updateSelection({ dateColumn: value || undefined });
    }

    function onAddFilter() {
      const next = [...userFilters.value, { field: '', operator: 'eq', value: '' }];
      updateSelection({ userFilters: next });
    }

    function onRemoveFilter(index: number) {
      const next = userFilters.value.filter((_, i) => i !== index);
      updateSelection({ userFilters: next });
    }

    function onUpdateFilterField(index: number, field: string) {
      const next = userFilters.value.map((f, i) =>
        i === index ? { ...f, field } : f,
      );
      updateSelection({ userFilters: next });
    }

    function onUpdateFilterOperator(index: number, operator: string) {
      const next = userFilters.value.map((f, i) =>
        i === index ? { ...f, operator } : f,
      );
      updateSelection({ userFilters: next });
    }

    function onUpdateFilterValue(index: number, value: string) {
      const next = userFilters.value.map((f, i) =>
        i === index ? { ...f, value } : f,
      );
      updateSelection({ userFilters: next });
    }

    return {
      t,
      selectedColumns,
      userFilters,
      visibleColumnGroups,
      dateColumnGroups,
      filterOperators,
      isUnaryOperator,
      onUpdateColumns,
      onUpdateDateColumn,
      onAddFilter,
      onRemoveFilter,
      onUpdateFilterField,
      onUpdateFilterOperator,
      onUpdateFilterValue,
    };
  },
});
